import React, { useState, useEffect } from 'react';
import api from '../../api';
import {
    Search,
    RefreshCw,
    Building2,
    MapPin,
    ExternalLink,
    BookOpen,
    Users
} from 'lucide-react';

function LMSCourseManager() {
    const [activeTab, setActiveTab] = useState('catalog');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    
    const [courses, setCourses] = useState([]);
    const [universities, setUniversities] = useState([]);
    const [pops, setPops] = useState([]);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isViewAssignmentsModalOpen, setIsViewAssignmentsModalOpen] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [fullDescCourse, setFullDescCourse] = useState(null);
    const [selectedUniIds, setSelectedUniIds] = useState([]);
    const [selectedPopIds, setSelectedPopIds] = useState({}); // { uni_id: [pop_ids] }
    const [selectedCourseIds, setSelectedCourseIds] = useState([]);
    const [assigning, setAssigning] = useState(false);
    const [toast, setToast] = useState({ message: '', type: '' });

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast({ message: '', type: '' }), 4000);
    };

    const fetchCourses = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/integrations/edvergencex/catalog');
            setCourses(res.data.data || []);
        } catch (err) {
            console.error("Error fetching courses:", err);
            setError("Failed to load course catalog.");
        } finally {
            setLoading(false);
        }
    };

    const fetchUniversities = async () => {
        try {
            const res = await api.get('/admin/universities');
            setUniversities(res.data || []);
        } catch (err) {
            console.error("Error fetching universities:", err);
        }
    };

    const fetchPops = async () => {
        try {
            const res = await api.get('/admin/pops');
            setPops(res.data || []);
        } catch (err) {
            console.error("Error fetching PoPs:", err);
        }
    };

    const handleAssignCourse = async () => {
        const isBulk = selectedCourseIds.length > 0;
        const coursesToAssign = isBulk ? selectedCourseIds : (selectedCourse ? [selectedCourse.externalContentRootId] : []);
        
        if (coursesToAssign.length === 0 || selectedUniIds.length === 0) return;
        setAssigning(true);
        try {
            const endpoint = isBulk ? '/integrations/edvergencex/assign-bulk-courses-to-uni' : '/integrations/edvergencex/assign-course-to-uni';
            
            // Filter out empty pop arrays
            const popAssignments = {};
            Object.keys(selectedPopIds).forEach(uniId => {
                if (selectedPopIds[uniId] && selectedPopIds[uniId].length > 0) {
                    popAssignments[uniId] = selectedPopIds[uniId];
                }
            });

            const payload = isBulk 
                ? { course_ids: coursesToAssign, university_ids: selectedUniIds.map(id => parseInt(id, 10)), pop_assignments: Object.keys(popAssignments).length > 0 ? popAssignments : undefined }
                : { course_id: coursesToAssign[0], university_ids: selectedUniIds.map(id => parseInt(id, 10)), pop_assignments: Object.keys(popAssignments).length > 0 ? popAssignments : undefined };

            const res = await api.post(endpoint, payload);
            showToast(res.data?.message || "Courses assigned successfully!");
            setIsAssignModalOpen(false);
            setSelectedCourse(null);
            setSelectedCourseIds([]);
            setSelectedUniIds([]);
            setSelectedPopIds({});
            // Refresh catalog to get updated assignment info
            fetchCourses();
        } catch (err) {
            console.error("Error assigning courses:", err);
            showToast(err.response?.data?.detail || "Failed to assign courses.", 'error');
        } finally {
            setAssigning(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'catalog') {
            fetchCourses();
            fetchUniversities();
            fetchPops();
        }
    }, [activeTab]);

    return (
        <div style={{ marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h2 style={{ fontSize: '1.25rem', margin: 0, fontWeight: 700 }}>Course Catalog</h2>
                    <p style={{ color: 'var(--text-muted)', margin: '4px 0 0', fontSize: '0.9rem' }}>Browse and assign external partner courses to universities.</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    {courses.length > 0 && (
                        <button
                            onClick={() => {
                                if (selectedCourseIds.length === courses.length) {
                                    setSelectedCourseIds([]);
                                } else {
                                    setSelectedCourseIds(courses.map(c => c.externalContentRootId));
                                }
                            }}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', background: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 600 }}
                        >
                            {selectedCourseIds.length === courses.length ? 'Deselect All' : 'Select All'}
                        </button>
                    )}
                    {selectedCourseIds.length > 0 && (
                        <button 
                            className="lms-btn-primary"
                            onClick={() => {
                                setSelectedCourse(null);
                                setIsAssignModalOpen(true);
                            }} 
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', background: 'var(--primary)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                        >
                            <Users size={16} />
                            <span>Bulk Assign ({selectedCourseIds.length})</span>
                        </button>
                    )}
                    <button 
                        onClick={fetchCourses} 
                        disabled={loading}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', background: 'var(--bg-card)', color: 'var(--text-main)', border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 600, width: 'fit-content' }}
                    >
                        <RefreshCw size={16} className={loading ? 'spin' : ''} />
                        <span>Refresh Catalog</span>
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Loading catalog...</div>
            ) : error ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
                    <p>{error}</p>
                    <button className="lms-btn-primary" onClick={fetchCourses} style={{ marginTop: '1rem' }}>Retry</button>
                </div>
            ) : courses.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                    <BookOpen size={48} style={{ color: 'var(--text-muted)', margin: '0 auto 16px' }} />
                    <h3 style={{ margin: '0 0 8px' }}>No Courses Found</h3>
                    <p style={{ color: 'var(--text-muted)', margin: 0 }}>The partner catalog is currently empty.</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                    {courses.map((course) => (
                        <div key={course.externalContentRootId} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                            <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
                                <input 
                                    type="checkbox" 
                                    checked={selectedCourseIds.includes(course.externalContentRootId)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedCourseIds(prev => [...prev, course.externalContentRootId]);
                                        } else {
                                            setSelectedCourseIds(prev => prev.filter(id => id !== course.externalContentRootId));
                                        }
                                    }}
                                    style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: 'var(--primary)' }}
                                />
                            </div>
                            <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
                                <img src={course.thumbnailUrl || 'https://via.placeholder.com/150'} alt={course.title} style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '8px' }} />
                            </div>
                            <div style={{ padding: '16px', flexGrow: 1 }}>
                                <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>{course.title}</h3>
                                <div style={{ flexGrow: 1, marginBottom: '16px' }}>
                                    <p className="course-card-description" style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>{course.description}</p>
                                    {course.description && course.description.length > 150 && (
                                        <button 
                                            onClick={() => setFullDescCourse(course)}
                                            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: '0.8rem', fontWeight: 600, marginTop: '4px' }}
                                        >
                                            Read More
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '11px', fontWeight: 600, background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '4px 8px', borderRadius: '6px' }}>{course.difficulty}</span>
                                    <span style={{ fontSize: '11px', fontWeight: 600, background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '4px 8px', borderRadius: '6px' }}>{course.estimatedMinutes} mins</span>
                                </div>
                                <div style={{ marginTop: '12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                    <strong style={{ color: 'var(--text-main)' }}>Assigned to:</strong>{' '}
                                    {course.assigned_universities && course.assigned_universities.length > 0 ? (
                                        <button 
                                            onClick={() => {
                                                setSelectedCourse(course);
                                                setIsViewAssignmentsModalOpen(true);
                                            }}
                                            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: '0.8rem', fontWeight: 600 }}
                                        >
                                            {course.assigned_universities.length} Universit{course.assigned_universities.length === 1 ? 'y' : 'ies'}
                                        </button>
                                    ) : (
                                        'None'
                                    )}
                                </div>
                            </div>
                            <div style={{ padding: '16px', paddingTop: 0 }}>
                                <button 
                                    className="lms-btn-primary" 
                                    style={{ width: '100%', justifyContent: 'center', padding: '12px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
                                    onClick={() => {
                                        setSelectedCourse(course);
                                        setIsAssignModalOpen(true);
                                    }}
                                >
                                    Assign to University
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isAssignModalOpen && (selectedCourse || selectedCourseIds.length > 0) && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'var(--bg-card)', width: '100%', maxWidth: '500px', borderRadius: '16px', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2 style={{ margin: 0, fontSize: '1.4rem' }}>Assign Course{selectedCourseIds.length > 0 && !selectedCourse ? 's' : ''}</h2>
                            <button onClick={() => setIsAssignModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}>&times;</button>
                        </div>
                        <p style={{ marginBottom: '20px' }}>
                            {selectedCourse 
                                ? <span>Assign <strong>{selectedCourse.title}</strong> to a university.</span> 
                                : <span>Assign <strong>{selectedCourseIds.length} selected courses</strong> to universities.</span>
                            }
                        </p>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: 'var(--text-main)' }}>Select Universities</label>
                            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px', background: 'var(--bg-main)' }}>
                                {universities.map(u => (
                                    <div key={u.id} style={{ borderBottom: '1px solid var(--border)', padding: '12px 8px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '10px' }}>
                                            <input 
                                                type="checkbox" 
                                                value={u.id}
                                                checked={selectedUniIds.includes(u.id.toString())}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedUniIds([...selectedUniIds, u.id.toString()]);
                                                    } else {
                                                        setSelectedUniIds(selectedUniIds.filter(id => id !== u.id.toString()));
                                                        // Remove pop assignments for this uni when deselected
                                                        const newPops = { ...selectedPopIds };
                                                        delete newPops[u.id];
                                                        setSelectedPopIds(newPops);
                                                    }
                                                }}
                                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                            />
                                            <span style={{ color: 'var(--text-main)', fontWeight: 600 }}>{u.institution_name}</span>
                                        </label>
                                        
                                        {selectedUniIds.includes(u.id.toString()) && (
                                            <div style={{ marginLeft: '28px', marginTop: '12px', background: 'var(--bg-card)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Assign Professors of Practice (Optional)</label>
                                                <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px', background: 'var(--bg-main)' }}>
                                                    {pops.map(p => (
                                                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', padding: '4px 6px', cursor: 'pointer', gap: '8px' }}>
                                                            <input 
                                                                type="checkbox"
                                                                checked={(selectedPopIds[u.id] || []).includes(p.id)}
                                                                onChange={(e) => {
                                                                    const currentPops = selectedPopIds[u.id] || [];
                                                                    if (e.target.checked) {
                                                                        setSelectedPopIds({ ...selectedPopIds, [u.id]: [...currentPops, p.id] });
                                                                    } else {
                                                                        setSelectedPopIds({ ...selectedPopIds, [u.id]: currentPops.filter(id => id !== p.id) });
                                                                    }
                                                                }}
                                                                style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                                                            />
                                                            <span style={{ fontSize: '0.85rem', color: 'var(--text-main)' }}>{p.institution_name}</span>
                                                        </label>
                                                    ))}
                                                    {pops.length === 0 && <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No PoPs found.</div>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {universities.length === 0 && <div style={{ padding: '8px', color: 'var(--text-muted)' }}>No universities found.</div>}
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button onClick={() => setIsAssignModalOpen(false)} style={{ padding: '10px 20px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                            <button 
                                onClick={handleAssignCourse} 
                                disabled={assigning || selectedUniIds.length === 0}
                                style={{ padding: '10px 20px', background: 'var(--primary)', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, opacity: (selectedUniIds.length === 0 || assigning) ? 0.6 : 1 }}
                            >
                                {assigning ? 'Assigning...' : `Assign to ${selectedUniIds.length} Uni${selectedUniIds.length === 1 ? '' : 's'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isViewAssignmentsModalOpen && selectedCourse && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'var(--bg-card)', width: '100%', maxWidth: '500px', borderRadius: '16px', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2 style={{ margin: 0, fontSize: '1.4rem' }}>Assigned Universities</h2>
                            <button onClick={() => setIsViewAssignmentsModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}>&times;</button>
                        </div>
                        <p style={{ marginBottom: '20px' }}><strong>{selectedCourse.title}</strong> is assigned to the following universities:</p>
                        <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px', background: 'var(--bg-main)' }}>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {selectedCourse.assigned_universities.map((uni, idx) => (
                                    <li key={idx} style={{ marginBottom: '8px', color: 'var(--text-main)' }}>{uni}</li>
                                ))}
                            </ul>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                            <button onClick={() => setIsViewAssignmentsModalOpen(false)} style={{ padding: '10px 20px', background: 'var(--primary)', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Close</button>
                        </div>
                    </div>
                </div>

            )}

            {/* Full Description Modal */}
            {fullDescCourse && (
                <div className="modal-overlay">
                    <div className="modal-box" style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h3>{fullDescCourse.title}</h3>
                            <button className="modal-close" onClick={() => setFullDescCourse(null)}>×</button>
                        </div>
                        <div className="modal-content custom-scrollbar" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <p style={{ color: 'var(--text-main)', fontSize: '0.95rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                {fullDescCourse.description}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Toasts */}
            <div style={{ position: 'fixed', bottom: '30px', right: '30px', zIndex: 999999, display: 'flex', flexDirection: 'column', gap: '12px', pointerEvents: 'none' }}>
                {toast.message && (
                <div style={{ background: toast.type === 'error' ? '#ef4444' : '#10b981', color: '#fff', padding: '16px 24px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', pointerEvents: 'auto' }}>
                    {toast.message}
                </div>
                )}
            </div>
        </div>
    );
}

export default LMSCourseManager;
